import { useEffect, useLayoutEffect } from 'react'
import { styled } from '@mui/material/styles'
import {
  Outlet,
  resetPathname,
  Route,
  Router,
  RouterPanel,
  Routes,
  Section,
  useNavigate,
  usePanelHistoryTop,
} from 'weblite-router'
import { safeExitFullscreen } from '@weblite/logic-toolkit'
import { ConditionalHooksPool } from '@weblite/use-conditional-hook'
import { handleLink } from '$logic/router/url'
import { useIsLoggedIn } from '$logic/auth/react-hook'
import MultiTab from '$comp/error-pages/multi-tab/multi-tab'
import { useIsAppLoaded, useIsMultiTab } from '$comp/main/react-hook'
import { isPhoneOrTablet, SIMULATED_PHONE_WIDTH } from '$func/environmentInfo'
import { ToastProvider } from '$comp/snackbars'
import {
  ModalsPanel,
  OverlayWappPanel,
  StoryPanel,
} from '$scenes/router-panels'
import { App } from '$scenes/components/app'
import { useIsDesktopSize } from '$common/react-hooks/layout'
import { ForceInstallTwa } from '$comp/force-install-twa'
import { ForceUpdateTwa } from '$comp/force-update-twa/force-update-twa'
import {
  useForceInstallTwaPageIsOpen,
  useNeedForceUpdateTwa,
} from '$logic/client-device/react-hook'
import { initializeCssVars } from '$setup/initialize-css-vars'
import { useStartup } from '$hooks/use-startup'
import { Auth } from './auth'
import {
  sendStartupProfilerEvent,
  StartupProfilerEventType,
} from '$logic/profiler'
import { checkForSynchronizePersistToResolveRace } from '$func/sync-versions'
import { StartUpModalQueue } from '$func/startup-modal-handler'
import { sendSignOutByServerAnalyticsEvent } from '$logic/auth/sign-out-analytics'
import { EmojiPickerPopover } from '$comp/modals/emoji-picker/emoji-picker'

const PanelsContainerRoot = styled('div')`
  position: relative;
  width: 100%;
  height: 100%;

  background-color: var(--safe-area-color, #fff);
  transition: background-color 250ms;
`

function PanelsContainer() {
  const isDesktop = useIsDesktopSize()
  const location = usePanelHistoryTop('root')?.location

  useLayoutEffect(() => {
    sendStartupProfilerEvent(StartupProfilerEventType.MOUNT_APP)
    initializeCssVars()
  }, [])

  return (
    <PanelsContainerRoot
      style={{
        ...(!isPhoneOrTablet && {
          width: SIMULATED_PHONE_WIDTH,
          margin: 'auto',
          boxShadow: 'rgb(0 0 0 / 5%) 0px 0px 20px 5px',
          overflow: 'hidden',
        }),

        ...(isDesktop && {
          width: '100%',
          maxWidth: location?.startsWith('root/auth') ? 'unset' : '1680px',
        }),
      }}
    >
      <Section name="overlay">
        <OverlayWappPanel.Portal data-name="overlayWapp">
          <RouterPanel />
        </OverlayWappPanel.Portal>

        <ModalsPanel.Portal data-name="modals">
          <RouterPanel />
        </ModalsPanel.Portal>
      </Section>

      <Section name="story" initialPanel="story">
        <StoryPanel.Portal data-name="story">
          <RouterPanel />
        </StoryPanel.Portal>
      </Section>

      <Outlet />

      <ToastProvider />
    </PanelsContainerRoot>
  )
}

export function Root() {
  const isLoaded = useIsAppLoaded()
  const isRunningInAnotherTab = useIsMultiTab()
  const needForceInstallTwa = useForceInstallTwaPageIsOpen()
  const needForceUpdateTwa = useNeedForceUpdateTwa()
  const isAuthenticated = useIsLoggedIn()
  const navigate = useNavigate()

  const isDesktop = useIsDesktopSize()

  useEffect(() => {
    console.log('isAppLoaded')
    if (isLoaded) {
      StartUpModalQueue.run()
      handleLink(window.location.href)
        .then(resetPathname)
        .then(checkForSynchronizePersistToResolveRace)
      StartUpModalQueue.run()

      // NOTE: If user refreshes app in fullscreen mode, it won't exit by default in some devices. so we need to call exitFullscreen to reset it
      safeExitFullscreen()
    }
  }, [isLoaded])

  useEffect(() => {
    if (isAuthenticated) {
      navigate('root/app', { absolute: true })
    } else {
      sendSignOutByServerAnalyticsEvent()
    }
  }, [isAuthenticated])

  useStartup()

  if (!isLoaded) return null

  return (
    <>
      <ConditionalHooksPool />
      <MultiTab isOpen={isRunningInAnotherTab} />
      <ForceInstallTwa isOpen={needForceInstallTwa} />
      <ForceUpdateTwa isOpen={needForceUpdateTwa} />
      <EmojiPickerPopover />
      <Router
        initialItem={{ location: 'root/auth/startingPage' }}
        options={{ disableAnimations: isDesktop }}
      >
        <Routes>
          <Route name="root" element={<PanelsContainer />}>
            <Route name="auth" element={<Auth />} />
            <Route name="app" element={<App />} />
          </Route>
        </Routes>
      </Router>
    </>
  )
}
